<script lang="ts" setup>
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  HousingApplicationStatus,
  HousingIntakeScenario,
  HousingIntakeScenarioProcessedApplicationStatus as PAS,
  HousingIntakeScenarioStatus,
  HousingOrgRoleName,
  type IntakeScenarioEntryDto,
  ScenarioWorkflowDto,
  WorkflowDto,
} from "~/api";
import { useMyHousingOrgRolesStore } from "~/YYC_Housing/stores/MyHousingOrgRolesStore";
import { useUnitTypeTree } from "~/composables/useUnitTypeTree";
import { useLoginAccountsStore } from "~/stores/LoginAccountsStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { formatDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";
import WorkflowResponseViewer from "~/Compliance/components/WorkflowResponseViewer.vue";

type ButtonDef = {
  label: string;
  workflowDefinitionName?: string;
  workflowType?: string;
  opensExistingType?: string;
  type?: "primary" | "warning" | "danger" | "default";
  wip?: boolean;
  isLock?: boolean;
  isMarkHoused?: boolean;
  isRemoveLock?: boolean;
  isUndoHoused?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

const props = defineProps<{
  scenario?: HousingIntakeScenario;
  fallbackScenarioEntry?: IntakeScenarioEntryDto;
  entityGuid: string;
  applicationGuid: string;
  processedStatus?: string | null;
  applicationStatus?: string | null;
  readOnly?: boolean;
  concurrencyReadOnly?: boolean;
  lockedElsewhere?: boolean;
}>();

const emit = defineEmits<{
  (e: "statusChanged"): void;
  (e: "done"): void;
  (e: "workflowCounts", counts: { noOffers: number; strikes: number }): void;
}>();

const myInfoStore = useMyInfoStore();
const loginAccountsStore = useLoginAccountsStore();
const myHousingOrgRolesStore = useMyHousingOrgRolesStore();
const { loadUnitTypeTree, getNodeName: getUnitTypeNodeName } =
  useUnitTypeTree();
const loading = ref(false);

const linkedWorkflows = ref<ScenarioWorkflowDto[]>([]);
const selectedWorkflow = ref<WorkflowDto | null>(null);
const workflowDrawerOpen = ref(false);
const inactiveExpanded = ref<string[]>([]);

const inactiveWorkflowTypes = new Set([
  "NoOffer",
  "CancelNoOffer",
  "Strike",
  "ThreeStrikes",
]);

const activeWorkflows = computed(() => {
  if (props.scenario) {
    return linkedWorkflows.value.filter(
      (w) => w.scenarioGuid === props.scenario!.housingIntakeScenarioGuid,
    );
  }
  return linkedWorkflows.value.filter(
    (w) =>
      !inactiveWorkflowTypes.has(w.workflowType ?? "") &&
      !w.workflow.workflowArchivedAt,
  );
});

const inactiveWorkflows = computed(() => {
  if (props.scenario) {
    return linkedWorkflows.value.filter(
      (w) => w.scenarioGuid !== props.scenario!.housingIntakeScenarioGuid,
    );
  }
  return linkedWorkflows.value.filter(
    (w) =>
      inactiveWorkflowTypes.has(w.workflowType ?? "") ||
      !!w.workflow.workflowArchivedAt,
  );
});

const hasPendingWorkflow = computed(() =>
  activeWorkflows.value.some((w) =>
    w.workflow.workflowStatus?.includes("Pending"),
  ),
);

const allUnitsFilled = computed(() => {
  const s = props.scenario;
  if (!s || s.status !== HousingIntakeScenarioStatus.OPEN) return false;
  const vacancies = s.vacancies ?? [];
  if (vacancies.length === 0) return false;
  const totalPlanned = vacancies.reduce(
    (sum, v) => sum + (v.totalUnits ?? 0),
    0,
  );
  const totalFilled = vacancies.reduce(
    (sum, v) => sum + (v.unitsFilled ?? 0),
    0,
  );
  return totalPlanned > 0 && totalFilled >= totalPlanned;
});

const noUnitsTooltip = "Scenario has no available units to make an offer";

const isClosedScenario = computed(
  () =>
    props.scenario?.status === HousingIntakeScenarioStatus.CLOSED ||
    props.scenario?.status === HousingIntakeScenarioStatus.ARCHIVED,
);

const hasIntakeRole = computed(() => {
  const orgGuid =
    props.scenario?.housingOrgGuid ??
    props.fallbackScenarioEntry?.housingOrgGuid;
  if (!orgGuid) return false;

  return myHousingOrgRolesStore.hasRole(orgGuid, HousingOrgRoleName.INTAKE);
});

const effectiveReadOnly = computed(
  () =>
    props.readOnly ||
    !hasIntakeRole.value ||
    (isClosedScenario.value && effectiveProcessedStatus.value !== PAS.ACCEPTED),
);

const effectiveProcessedStatus = computed(
  () => props.processedStatus ?? props.fallbackScenarioEntry?.status ?? null,
);

const isHoused = computed(
  () =>
    effectiveProcessedStatus.value === PAS.ACCEPTED &&
    props.applicationStatus === HousingApplicationStatus.HOUSED,
);

const currentStatus = computed<PAS | "Housed" | null>(() => {
  if (isHoused.value) return "Housed";
  const ps = (effectiveProcessedStatus.value as PAS) ?? null;
  if (ps === PAS.NO_OFFER_CANCELLED) return null;
  if (ps) return ps;
  if (
    !props.scenario &&
    props.applicationStatus === HousingApplicationStatus.HOUSED
  )
    return "Housed";
  return null;
});

const buttons = computed<ButtonDef[]>(() => {
  const s = currentStatus.value;
  if (!s) {
    return [
      { label: "Lock for Interview", type: "primary", isLock: true },
      {
        label: "No Offer",
        type: "warning",
        workflowDefinitionName: "No Offer Form",
        workflowType: "NoOffer",
      },
      { label: "Done", type: "default" },
    ];
  }
  switch (s) {
    case PAS.INTERVIEWING:
      return [
        {
          label: "Make Offer",
          type: "primary",
          workflowDefinitionName: "Offer Form",
          workflowType: "Offer",
          disabled: allUnitsFilled.value,
          tooltip: allUnitsFilled.value ? noUnitsTooltip : undefined,
        },
        {
          label: "No Offer",
          type: "warning",
          workflowDefinitionName: "No Offer Form",
          workflowType: "NoOffer",
        },
        { label: "Remove Lock", type: "warning", isRemoveLock: true },
        {
          label: "Strike",
          type: "warning",
          workflowDefinitionName: "1 Strike Form",
          workflowType: "Strike",
          disabled: strikeCount.value >= 2,
          tooltip:
            strikeCount.value >= 2
              ? "This applicant already has 2 strikes"
              : undefined,
        },
        {
          label: "3 Strikes - Cancel Application",
          type: "danger",
          workflowDefinitionName: "3 Strike Form",
          workflowType: "ThreeStrikes",
        },
        { label: "Done", type: "default" },
      ];
    case PAS.INTAKE:
      return [
        {
          label: "Make Offer",
          type: "primary",
          workflowDefinitionName: "Offer Form",
          workflowType: "Offer",
          disabled: allUnitsFilled.value,
          tooltip: allUnitsFilled.value ? noUnitsTooltip : undefined,
        },
        {
          label: "No Offer",
          type: "warning",
          workflowDefinitionName: "No Offer Form",
          workflowType: "NoOffer",
        },
        { label: "Remove Lock", type: "warning", isRemoveLock: true },
        {
          label: "Strike",
          type: "warning",
          workflowDefinitionName: "1 Strike Form",
          workflowType: "Strike",
          disabled: strikeCount.value >= 2,
          tooltip:
            strikeCount.value >= 2
              ? "This applicant already has 2 strikes"
              : undefined,
        },
        {
          label: "3 Strikes - Cancel Application",
          type: "danger",
          workflowDefinitionName: "3 Strike Form",
          workflowType: "ThreeStrikes",
        },
        { label: "Done", type: "default" },
      ];
    case PAS.OFFER_IN_PROGRESS:
      return [
        {
          label: "Accept or Reject Offer",
          type: "primary",
          opensExistingType: "Offer",
        },
        {
          label: "Modify Offer",
          type: "default",
          opensExistingType: "Offer",
        },
        {
          label: "Strike",
          type: "warning",
          workflowDefinitionName: "1 Strike Form",
          workflowType: "Strike",
          disabled: strikeCount.value >= 2,
          tooltip:
            strikeCount.value >= 2
              ? "This applicant already has 2 strikes"
              : undefined,
        },
        {
          label: "3 Strikes - Cancel",
          type: "danger",
          workflowDefinitionName: "3 Strike Form",
          workflowType: "ThreeStrikes",
        },
        { label: "Done", type: "default" },
      ];
    case PAS.ACCEPTED:
      return [
        { label: "Housed", type: "primary", isMarkHoused: true },
        {
          label: "Strike",
          type: "warning",
          workflowDefinitionName: "1 Strike Form",
          workflowType: "Strike",
          disabled: strikeCount.value >= 2,
          tooltip:
            strikeCount.value >= 2
              ? "This applicant already has 2 strikes"
              : undefined,
        },
        {
          label: "3 Strikes - Cancel",
          type: "danger",
          workflowDefinitionName: "3 Strike Form",
          workflowType: "ThreeStrikes",
        },
        { label: "Done", type: "default" },
      ];
    case "Housed":
      return [
        { label: "Undo Housed", type: "warning", isUndoHoused: true },
        {
          label: "3 Strikes - Cancel",
          type: "danger",
          workflowDefinitionName: "3 Strike Form",
          workflowType: "ThreeStrikes",
        },
        { label: "Done", type: "default" },
      ];
    case PAS.NO_OFFER:
      return [
        {
          label: "Cancel No Offer",
          type: "warning",
          workflowDefinitionName: "Cancel No Offer Form",
          workflowType: "CancelNoOffer",
        },
        { label: "Done", type: "default" },
      ];
    case PAS.OFFER_REJECTED:
      return [
        {
          label: "Update Offer Acceptance",
          type: "primary",
          opensExistingType: "Offer",
        },
        { label: "Done", type: "default" },
      ];
    case PAS.REJECTED:
      return [{ label: "Done", type: "default" }];
    default:
      return [{ label: "Done", type: "default" }];
  }
});

const workflowTypeDisplayNames: Record<string, string> = {
  NoOffer: "No Offer",
  CancelNoOffer: "Cancel No Offer",
  Offer: "Offer",
  Strike: "Strike",
  ThreeStrikes: "3 Strikes",
  Housed: "Housed",
};

const buildWorkflowName = (workflowType?: string): string => {
  const formName = workflowType
    ? (workflowTypeDisplayNames[workflowType] ?? workflowType)
    : "Form";
  const siteName = props.scenario?.housingSite?.name ?? "";
  const unitType = props.scenario?.unitTypeLeafNodeGuid
    ? getUnitTypeNodeName(props.scenario.unitTypeLeafNodeGuid)
    : "";
  const parts = [formName, siteName, unitType].filter(Boolean);
  return parts.join(" - ");
};

const loadLinkedWorkflows = async () => {
  try {
    linkedWorkflows.value =
      await apiClient.housingIntakeScenario.getAllScenarioWorkflowsForApplication(
        myInfoStore.currentSysOrgGuid,
        props.applicationGuid,
        props.entityGuid,
      );
    emitWorkflowCounts();
  } catch (e) {
    showError(e);
  }
};

const recordedStrikeStatuses = new Set([
  "StrikeRecorded",
  "ThreeStrikesRecorded",
]);

const strikeCount = computed(
  () =>
    linkedWorkflows.value.filter(
      (w) =>
        (w.workflowType === "Strike" || w.workflowType === "ThreeStrikes") &&
        recordedStrikeStatuses.has(w.workflow.workflowStatus) &&
        !w.workflow.workflowArchivedAt,
    ).length,
);

const emitWorkflowCounts = () => {
  const noOffers = linkedWorkflows.value.filter(
    (w) => w.workflowType === "NoOffer",
  ).length;
  emit("workflowCounts", { noOffers, strikes: strikeCount.value });
};

const handleButtonClick = async (button: ButtonDef) => {
  if (button.label === "Done") {
    emit("done");
    return;
  }
  if (button.wip) {
    ElMessage.info("Work in Progress");
    return;
  }

  const effectiveScenarioGuid =
    props.scenario?.housingIntakeScenarioGuid ??
    props.fallbackScenarioEntry?.scenarioGuid;

  if (!effectiveScenarioGuid) return;

  loading.value = true;
  try {
    if (button.isLock) {
      await apiClient.housingIntakeScenario.lockForInterview(
        effectiveScenarioGuid,
        props.applicationGuid,
      );
      emit("statusChanged");
      return;
    }

    if (button.isMarkHoused) {
      await apiClient.housingIntakeScenario.markHoused(
        effectiveScenarioGuid,
        props.applicationGuid,
      );
      emit("statusChanged");
      return;
    }

    if (button.isRemoveLock) {
      await apiClient.housingIntakeScenario.removeLock(
        effectiveScenarioGuid,
        props.applicationGuid,
      );
      emit("statusChanged");
      return;
    }

    if (button.isUndoHoused) {
      await apiClient.housingIntakeScenario.undoHoused(
        effectiveScenarioGuid,
        props.applicationGuid,
      );
      emit("statusChanged");
      return;
    }

    if (button.opensExistingType) {
      const existing = linkedWorkflows.value.find(
        (w) => w.workflowType === button.opensExistingType,
      );
      if (existing) {
        selectedWorkflow.value = existing.workflow;
        workflowDrawerOpen.value = true;
      } else {
        ElMessage.warning(
          `No existing ${button.opensExistingType} workflow found.`,
        );
      }
      return;
    }

    if (button.workflowDefinitionName) {
      const newWorkflow =
        await apiClient.workflowInstance.createWorkflowWithLinks({
          workflowDefinitionName: button.workflowDefinitionName,
          workflowName: buildWorkflowName(button.workflowType),
          orgGuid: myInfoStore.currentSysOrgGuid,
          primaryEntityGuid: props.entityGuid,
          linkedEntities: [
            { entityGuid: props.entityGuid, linkType: "HousingApplication" },
          ],
          housingIntakeScenarioGuid: effectiveScenarioGuid,
          housingIntakeScenarioApplicationGuid: props.applicationGuid,
          housingIntakeScenarioWorkflowType: button.workflowType,
        });

      await loadLinkedWorkflows();
      selectedWorkflow.value = newWorkflow;
      workflowDrawerOpen.value = true;
    }
  } catch (e: any) {
    showError(e);
    if (e?.status === 409) {
      emit("statusChanged");
    }
  } finally {
    loading.value = false;
  }
};

const handleWorkflowDrawerClose = async () => {
  workflowDrawerOpen.value = false;
  selectedWorkflow.value = null;
  await loadLinkedWorkflows();
  emit("statusChanged");
};
const handleWorkflowUpdated = async () => {
  await loadLinkedWorkflows();
  emit("statusChanged");
};

const openLinkedWorkflow = (link: ScenarioWorkflowDto) => {
  selectedWorkflow.value = link.workflow;
  workflowDrawerOpen.value = true;
};

const handleOpenFollowUpWorkflow = async (workflowGuid: string) => {
  try {
    const content = await apiClient.workflowInstance.getWorkflow(workflowGuid);
    selectedWorkflow.value = {
      guid: content.workflow.guid,
      workflowStatus: content.workflow.status ?? "",
      workflowName: content.workflow.name ?? "",
      workflowArchivedAt: content.workflow.archivedAt ?? "",
      workflowDefinitionName: content.workflow.workflowDefinition?.name ?? "",
      workflowDefinitionGuid: content.workflow.workflowDefinition?.guid ?? "",
      ownerName: "",
      ownerGuid: content.workflow.entityGuid ?? undefined,
      answersToShow: {},
      updatedAt: content.workflow.createdAt ?? "",
    };
    workflowDrawerOpen.value = true;
  } catch (e) {
    showError(e);
  }
};

onMounted(async () => {
  await Promise.all([
    loginAccountsStore.load(),
    loadUnitTypeTree(),
    loadLinkedWorkflows(),
    myHousingOrgRolesStore.load(),
  ]);
});
</script>

<template>
  <div class="intake-action-panel">
    <!-- Pending workflow warning -->
    <el-alert
      v-if="hasPendingWorkflow && (!effectiveReadOnly || concurrencyReadOnly)"
      type="warning"
      :closable="false"
      show-icon
    >
      Cannot take further actions until pending form is complete. If this was
      created by accident you can open the form and cancel it.
    </el-alert>

    <!-- Action Buttons -->
    <div
      v-if="!effectiveReadOnly || concurrencyReadOnly"
      class="action-buttons"
    >
      <el-tooltip
        v-for="button in buttons"
        :key="button.label"
        :content="button.tooltip"
        :disabled="!button.tooltip"
        placement="top"
      >
        <el-button
          :type="button.type ?? 'default'"
          :loading="loading && !button.wip && button.label !== 'Done'"
          :disabled="
            button.disabled ||
            concurrencyReadOnly ||
            (hasPendingWorkflow && button.label !== 'Done')
          "
          size="default"
          @click="handleButtonClick(button)"
        >
          {{ button.label }}
        </el-button>
      </el-tooltip>
    </div>
    <div
      v-else-if="scenario && !isClosedScenario"
      class="intake-action-placeholder"
    >
      <span v-if="lockedElsewhere">
        This applicant is locked in another scenario</span
      >

      <span v-else>
        This applicant is not next in line for intake. Intake actions are only
        available for the top eligible applicant via
        <strong>Review Fit</strong>.</span
      >
    </div>

    <!-- Active Workflows Table -->
    <div v-if="activeWorkflows.length > 0" class="forms-table-section">
      <h4>Vacancy Management Forms</h4>
      <el-table :data="activeWorkflows" stripe size="small" style="width: 100%">
        <el-table-column label="Form Type" width="600">
          <template #default="{ row }: { row: ScenarioWorkflowDto }">
            <el-button link type="primary" @click="openLinkedWorkflow(row)">
              {{ row.workflow.workflowName }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column label="Status" width="250">
          <template #default="{ row }: { row: ScenarioWorkflowDto }">
            <el-tag size="small">{{ row.workflow.workflowStatus }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Date Created" width="150">
          <template #default="{ row }: { row: ScenarioWorkflowDto }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="Created By" width="150">
          <template #default="{ row }: { row: ScenarioWorkflowDto }">
            {{ loginAccountsStore.getName(row.workflow.createdByUserGuid) }}
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Inactive Workflows (collapsed) -->
    <el-collapse
      v-if="inactiveWorkflows.length > 0"
      v-model="inactiveExpanded"
      class="inactive-workflows-collapse"
      expand-icon-position="left"
    >
      <el-collapse-item name="inactive">
        <template #title>
          <span class="inactive-title">{{
            `Past Vacancy Management Forms (${inactiveWorkflows.length})`
          }}</span>
        </template>
        <el-table
          :data="inactiveWorkflows"
          stripe
          size="small"
          style="width: 100%"
        >
          <el-table-column label="Form Type" width="450">
            <template #default="{ row }: { row: ScenarioWorkflowDto }">
              <el-button link type="primary" @click="openLinkedWorkflow(row)">
                {{ row.workflow.workflowName }}
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="Status" width="250">
            <template #default="{ row }: { row: ScenarioWorkflowDto }">
              <el-tag size="small">{{ row.workflow.workflowStatus }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Date Created" width="150">
            <template #default="{ row }: { row: ScenarioWorkflowDto }">
              {{ formatDate(row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column label="Created By" width="150">
            <template #default="{ row }: { row: ScenarioWorkflowDto }">
              {{ loginAccountsStore.getName(row.workflow.createdByUserGuid) }}
            </template>
          </el-table-column>
        </el-table>
      </el-collapse-item>
    </el-collapse>

    <!-- Workflow Viewer Drawer -->
    <WorkflowResponseViewer
      v-if="selectedWorkflow"
      :compliance-workflow="selectedWorkflow"
      :full-screen="false"
      @close-drawer="handleWorkflowDrawerClose"
      @update-workflow="handleWorkflowUpdated"
      @refresh-list="handleWorkflowDrawerClose"
      @open-follow-up-workflow="handleOpenFollowUpWorkflow"
    />
  </div>
</template>

<style lang="less">
.housing-application-drawer {
  .intake-action-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .action-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .forms-table-section h4 {
      margin: 0 0 8px 0;
      font-size: 14px;
      font-weight: 600;
      color: var(--el-text-color-primary);
    }

    .inactive-workflows-collapse.el-collapse {
      --el-collapse-header-bg-color: transparent;
      --el-collapse-content-bg-color: transparent;
      border: 0;

      .el-collapse-item__header {
        background-color: transparent !important;
        box-shadow: none;
      }
    }

    .inactive-title {
      font-size: 13px;
      font-weight: 600;
      color: var(--el-text-color-secondary);
    }

    .intake-action-placeholder {
      padding: 12px;
      color: var(--el-text-color-secondary);
      font-size: 0.85rem;
      text-align: center;
      border: 1px dashed var(--el-border-color);
      border-radius: 4px;
    }
  }
}
</style>
